import { CbhIcon, Input, Text } from "@clipboard-health/ui-components";
import { Box, Stack } from "@mui/material";
import { type Address, type Location } from "@src/appV2/lib/GoogleMaps";
import { getPlaceDetails } from "@src/appV2/lib/GoogleMaps/googleMaps";
import { useToast } from "@src/appV2/lib/Notifications/Toasts/useToast";
import { useEffect, useRef, useState } from "react";

import { useAddressAutocomplete } from "../hooks/useAddressAutocomplete";

interface AddressAutocompleteInputProps {
  addressInputValue: string;
  onAddressInputChange: (value: string) => void;
  onPlaceChanged: (params: { address: Address; location: Location }) => void;
  errorMessage?: string;
  shouldFocusOnRender?: boolean;
  disabled?: boolean;
}

export function AddressAutocompleteInput(props: AddressAutocompleteInputProps) {
  const {
    addressInputValue,
    onAddressInputChange,
    onPlaceChanged,
    errorMessage,
    shouldFocusOnRender,
    disabled,
  } = props;

  const [isFocused, setIsFocused] = useState<boolean>(false);
  const [hasFocused, setHasFocused] = useState<boolean>(false);

  const { showErrorToast } = useToast();

  const inputRef = useRef<HTMLInputElement>(null);

  const { predictions, hasNoResults, hasError } = useAddressAutocomplete({
    query: addressInputValue,
    debounceMs: 300,
  });

  // Focus on render
  useEffect(() => {
    if (shouldFocusOnRender && inputRef.current && !hasFocused) {
      inputRef.current.focus();
      setHasFocused(true);
    }
  }, [shouldFocusOnRender, inputRef, hasFocused]);

  return (
    <Box>
      <Input
        fullWidth
        inputRef={inputRef}
        inputProps={{
          "aria-label": "Manual Address Input",
        }}
        value={addressInputValue}
        disabled={disabled}
        size="large"
        error={Boolean(errorMessage)}
        onChange={(event) => {
          onAddressInputChange(event.target.value);
        }}
        onFocus={() => {
          setIsFocused(true);
        }}
        onBlur={() => {
          // This is to prevent the dropdown from closing immediately when the user clicks on a suggestion
          setTimeout(() => {
            setIsFocused(false);
          }, 1000);
        }}
      />

      <Stack ml={2}>
        {errorMessage && (
          <Text variant="caption" color={(theme) => theme.palette.intent?.error.text} mt={2}>
            {errorMessage}
          </Text>
        )}

        {predictions.length > 0 && isFocused && (
          <Stack spacing={6} mt={6}>
            {predictions.map((prediction) => {
              return (
                <Stack
                  key={prediction.place_id}
                  direction="row"
                  justifyContent="space-between"
                  sx={{ cursor: "pointer" }}
                  role="button"
                  aria-label={`Address suggestion: ${prediction.description}`}
                  onClick={async () => {
                    try {
                      const { address, location } = await getPlaceDetails(prediction.place_id);
                      onPlaceChanged({ address, location });
                    } catch {
                      showErrorToast("Failed to load address details");
                    }

                    setIsFocused(false);
                  }}
                >
                  <Text semibold color={(theme) => theme.palette.text.primary} variant="h6">
                    {prediction.description}
                  </Text>
                  <CbhIcon
                    type="arrow-up"
                    sx={{
                      transform: "rotate(45deg)",
                    }}
                  />
                </Stack>
              );
            })}
          </Stack>
        )}

        {(hasNoResults || hasError) && isFocused && (
          <Text semibold variant="body2" color={(theme) => theme.palette.intent?.error.text} mt={2}>
            {hasNoResults
              ? "We cannot find this address. Try another one"
              : "Unable to load address suggestions"}
          </Text>
        )}
      </Stack>
    </Box>
  );
}
